import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Calculator, DollarSign, Clock, TrendingUp, Users, Building2, GraduationCap, Briefcase, Monitor, ShoppingBag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

/* ═══════ CONSTANTS ═══════ */

const WORKING_HOURS_YEAR = 2080;
const REALISATION_FACTOR = 0.4; // Fraction of saved hours that converts to actual recovered cost (CFO-defensible)
const ROI_DISPLAY_CAP = 999; // Cap displayed ROI for credibility; net gain is shown uncapped

const industries = [
  { key: 'saas', label: 'Technology & SaaS', icon: Monitor },
  { key: 'finance', label: 'Financial Services', icon: Building2 },
  { key: 'b2c', label: 'B2C Companies', icon: ShoppingBag },
  { key: 'consulting', label: 'IT Services & Consulting', icon: Briefcase },
  { key: 'edtech', label: 'EdTech & Corporate Training', icon: GraduationCap },
];

const pricingModels = [
  { key: 'seat', label: 'Seat-Based' },
  { key: 'account', label: 'Account-Based' },
  { key: 'credit', label: 'Credit-Based' },
];

const seatTiers = [
  { label: 'Starter (1-10)', min: 1, max: 10, rate: 79 },
  { label: 'Growth (11-50)', min: 11, max: 50, rate: 99 },
  { label: 'Pro (51-200)', min: 51, max: 200, rate: 129 },
  { label: 'Scale (200+)', min: 201, max: 9999, rate: 159 },
];

const creditPacks = [
  { key: 'starter', label: 'Starter', price: 99, credits: 50000 },
  { key: 'growth', label: 'Growth', price: 399, credits: 250000 },
  { key: 'scale', label: 'Scale', price: 1299, credits: 1000000 },
  { key: 'payg', label: 'Pay-as-you-go', price: 0, credits: 0 },
];

/* ═══════ DEFAULTS PER INDUSTRY ═══════
   - winRateImprovement / conversionImprovement = RELATIVE lift on the baseline rate
   - churnReduction = relative reduction of existing churn
   - baselineWinRate / baselineConversion = current performance baseline (not a slider)
   Defaults calibrated to defensible enterprise benchmarks (CFO-safe). */

const industryDefaults = {
  saas: {
    annualPipeline: 5000000, baselineWinRate: 0.22, winRateImprovement: 0.08,
    numReps: 20, hoursSaved: 4, annualRepCost: 120000,
  },
  finance: {
    annualPipeline: 8000000, baselineWinRate: 0.18, winRateImprovement: 0.07,
    totalClientRevenue: 20000000, churnRate: 0.08, churnReduction: 0.10,
    activeClients: 200, expansionPct: 0.06, avgExpansionRev: 15000,
    numRMs: 15, hoursSaved: 3, annualRMCost: 140000,
    monthlyReportingHours: 80, hourlyReportingRate: 75,
  },
  b2c: {
    leadPipeline: 3000000, baselineConversion: 0.18, conversionImprovement: 0.08,
    annualRecurring: 10000000, churnRate: 0.10, churnReduction: 0.10,
    activeCustomers: 5000, upsellPct: 0.04, avgUpsellValue: 200,
    partnerPipeline: 1000000, baselineChannelWinRate: 0.20, channelWinImprovement: 0.06,
    staffCount: 25, hoursSaved: 3, annualStaffCost: 80000,
  },
  consulting: {
    proposalPipeline: 6000000, baselineWinRate: 0.20, winRateImprovement: 0.07,
    totalConsultants: 50, benchDaysSaved: 4, dailyBillingRate: 1200,
    activeAccounts: 80, expansionPct: 0.07, avgExpansionRev: 25000,
    numSellers: 12, hoursSaved: 4, annualSellerCost: 130000,
  },
  edtech: {
    newBusinessPipeline: 4000000, baselineWinRate: 0.20, winRateImprovement: 0.08,
    renewalARR: 8000000, churnRate: 0.12, churnReduction: 0.10,
    activeAccounts: 150, expansionPct: 0.05, avgExpansionRev: 8000,
    staffCount: 18, hoursSaved: 3.5, annualStaffCost: 90000,
  },
};

/* ═══════ HELPERS ═══════ */

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtPct = (n) => `${Math.round(n * 100)}%`;

function SliderInput({ label, value, onChange, min, max, step = 1, format = 'number', suffix = '' }) {
  const display = format === 'currency' ? fmt(value) : format === 'percent' ? fmtPct(value) : `${value}${suffix}`;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <span className="text-sm font-bold text-blue-600">{display}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={format === 'percent' ? value * 100 : value}
        onChange={(e) => {
          const v = Number(e.target.value);
          onChange(format === 'percent' ? v / 100 : v);
        }}
        className="w-full accent-blue-600 h-2"
      />
    </div>
  );
}

/* ═══════ COST CALCULATOR ═══════ */

function calcCost(model, params) {
  if (model === 'seat') {
    const seats = params.seats || 20;
    const tier = seatTiers.find(t => seats >= t.min && seats <= t.max) || seatTiers[3];
    return seats * tier.rate * 12;
  }
  if (model === 'account') {
    const accounts = params.accounts || 50;
    const rate = params.billingCycle === 'annual' ? 199 : 239;
    return accounts * rate * 12;
  }
  if (model === 'credit') {
    const pack = creditPacks.find(p => p.key === params.creditPack) || creditPacks[1];
    if (pack.key === 'payg') {
      const monthlyCredits = params.monthlyCredits || 10000;
      return monthlyCredits * 0.003 * 12;
    }
    return pack.price * 12;
  }
  return 0;
}

/* ═══════ BENEFIT CALCULATORS ═══════
   Math:
   - Pipeline / Conversion gain = pipeline × baseline_rate × relative_lift
     (NOT pipeline × lift, that over-counts by ~5×)
   - Productivity gain = hours_saved × hourly_rate × REALISATION_FACTOR
     (CFO-defensible, soft savings discounted)
   - Churn / Retention gain = revenue_base × current_churn × reduction_of_churn
   - Expansion / Upsell = base × incremental_pct × avg_deal */

function calcBenefitsSaaS(d) {
  const pipeline = d.annualPipeline * d.baselineWinRate * d.winRateImprovement;
  const productivity = d.numReps * d.hoursSaved * 52 * (d.annualRepCost / WORKING_HOURS_YEAR) * REALISATION_FACTOR;
  return {
    items: [
      { label: 'Pipeline Gain', value: pipeline },
      { label: 'Productivity Gain', value: productivity },
    ],
    total: pipeline + productivity,
  };
}

function calcBenefitsFinance(d) {
  const pipeline = d.annualPipeline * d.baselineWinRate * d.winRateImprovement;
  const retention = d.totalClientRevenue * d.churnRate * d.churnReduction;
  const walletShare = d.activeClients * d.expansionPct * d.avgExpansionRev;
  const productivity = d.numRMs * d.hoursSaved * 52 * (d.annualRMCost / WORKING_HOURS_YEAR) * REALISATION_FACTOR;
  const reporting = d.monthlyReportingHours * 12 * d.hourlyReportingRate * REALISATION_FACTOR;
  return {
    items: [
      { label: 'Pipeline Gain', value: pipeline },
      { label: 'Retention Gain', value: retention },
      { label: 'Wallet Share Gain', value: walletShare },
      { label: 'Productivity Gain', value: productivity },
      { label: 'Reporting Gain', value: reporting },
    ],
    total: pipeline + retention + walletShare + productivity + reporting,
  };
}

function calcBenefitsB2C(d) {
  const acquisition = d.leadPipeline * d.baselineConversion * d.conversionImprovement;
  const retention = d.annualRecurring * d.churnRate * d.churnReduction;
  const upsell = d.activeCustomers * d.upsellPct * d.avgUpsellValue;
  const channel = d.partnerPipeline * d.baselineChannelWinRate * d.channelWinImprovement;
  const productivity = d.staffCount * d.hoursSaved * 52 * (d.annualStaffCost / WORKING_HOURS_YEAR) * REALISATION_FACTOR;
  return {
    items: [
      { label: 'Acquisition Gain', value: acquisition },
      { label: 'Retention Gain', value: retention },
      { label: 'Upsell Gain', value: upsell },
      { label: 'Channel Gain', value: channel },
      { label: 'Productivity Gain', value: productivity },
    ],
    total: acquisition + retention + upsell + channel + productivity,
  };
}

function calcBenefitsConsulting(d) {
  const pipeline = d.proposalPipeline * d.baselineWinRate * d.winRateImprovement;
  const utilization = d.totalConsultants * d.benchDaysSaved * d.dailyBillingRate;
  const expansion = d.activeAccounts * d.expansionPct * d.avgExpansionRev;
  const productivity = d.numSellers * d.hoursSaved * 52 * (d.annualSellerCost / WORKING_HOURS_YEAR) * REALISATION_FACTOR;
  return {
    items: [
      { label: 'Pipeline Gain', value: pipeline },
      { label: 'Utilization Gain', value: utilization },
      { label: 'Account Expansion Gain', value: expansion },
      { label: 'Productivity Gain', value: productivity },
    ],
    total: pipeline + utilization + expansion + productivity,
  };
}

function calcBenefitsEdTech(d) {
  const pipeline = d.newBusinessPipeline * d.baselineWinRate * d.winRateImprovement;
  const retention = d.renewalARR * d.churnRate * d.churnReduction;
  const expansion = d.activeAccounts * d.expansionPct * d.avgExpansionRev;
  const productivity = d.staffCount * d.hoursSaved * 52 * (d.annualStaffCost / WORKING_HOURS_YEAR) * REALISATION_FACTOR;
  return {
    items: [
      { label: 'Pipeline Gain', value: pipeline },
      { label: 'Retention Gain', value: retention },
      { label: 'Expansion Gain', value: expansion },
      { label: 'Productivity Gain', value: productivity },
    ],
    total: pipeline + retention + expansion + productivity,
  };
}

const benefitCalcs = {
  saas: calcBenefitsSaaS,
  finance: calcBenefitsFinance,
  b2c: calcBenefitsB2C,
  consulting: calcBenefitsConsulting,
  edtech: calcBenefitsEdTech,
};

/* ═══════ INDUSTRY INPUT PANELS ═══════ */

function SaaSInputs({ data, set }) {
  const u = (k, v) => set({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <SliderInput label="Annual Pipeline Value" value={data.annualPipeline} onChange={v => u('annualPipeline', v)} min={500000} max={50000000} step={500000} format="currency" />
      <SliderInput label="Win Rate Lift (relative)" value={data.winRateImprovement} onChange={v => u('winRateImprovement', v)} min={3} max={20} step={1} format="percent" />
      <SliderInput label="Number of Reps" value={data.numReps} onChange={v => u('numReps', v)} min={1} max={200} />
      <SliderInput label="Hours Saved / Rep / Week" value={data.hoursSaved} onChange={v => u('hoursSaved', v)} min={1} max={10} step={0.5} suffix=" hrs" />
      <SliderInput label="Annual Rep Cost" value={data.annualRepCost} onChange={v => u('annualRepCost', v)} min={40000} max={300000} step={5000} format="currency" />
    </div>
  );
}

function FinanceInputs({ data, set }) {
  const u = (k, v) => set({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <SliderInput label="Annual Deal Pipeline" value={data.annualPipeline} onChange={v => u('annualPipeline', v)} min={1000000} max={100000000} step={1000000} format="currency" />
      <SliderInput label="Win Rate Lift (relative)" value={data.winRateImprovement} onChange={v => u('winRateImprovement', v)} min={3} max={20} step={1} format="percent" />
      <SliderInput label="Total Client Revenue" value={data.totalClientRevenue} onChange={v => u('totalClientRevenue', v)} min={1000000} max={100000000} step={1000000} format="currency" />
      <SliderInput label="Churn Rate" value={data.churnRate} onChange={v => u('churnRate', v)} min={1} max={25} step={1} format="percent" />
      <SliderInput label="Churn Reduction (relative)" value={data.churnReduction} onChange={v => u('churnReduction', v)} min={3} max={20} step={1} format="percent" />
      <SliderInput label="Active Clients" value={data.activeClients} onChange={v => u('activeClients', v)} min={10} max={1000} step={10} />
      <SliderInput label="Expansion %" value={data.expansionPct} onChange={v => u('expansionPct', v)} min={1} max={15} step={1} format="percent" />
      <SliderInput label="Avg Expansion Revenue" value={data.avgExpansionRev} onChange={v => u('avgExpansionRev', v)} min={1000} max={100000} step={1000} format="currency" />
      <SliderInput label="Number of RMs" value={data.numRMs} onChange={v => u('numRMs', v)} min={1} max={100} />
      <SliderInput label="Hours Saved / RM / Week" value={data.hoursSaved} onChange={v => u('hoursSaved', v)} min={1} max={10} step={0.5} suffix=" hrs" />
      <SliderInput label="Annual RM Cost" value={data.annualRMCost} onChange={v => u('annualRMCost', v)} min={50000} max={300000} step={5000} format="currency" />
      <SliderInput label="Monthly Reporting Hours" value={data.monthlyReportingHours} onChange={v => u('monthlyReportingHours', v)} min={10} max={300} step={10} />
      <SliderInput label="Hourly Reporting Rate" value={data.hourlyReportingRate} onChange={v => u('hourlyReportingRate', v)} min={25} max={200} step={5} format="currency" />
    </div>
  );
}

function B2CInputs({ data, set }) {
  const u = (k, v) => set({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <SliderInput label="Lead Pipeline Value" value={data.leadPipeline} onChange={v => u('leadPipeline', v)} min={500000} max={50000000} step={500000} format="currency" />
      <SliderInput label="Conversion Lift (relative)" value={data.conversionImprovement} onChange={v => u('conversionImprovement', v)} min={3} max={20} step={1} format="percent" />
      <SliderInput label="Annual Recurring Revenue" value={data.annualRecurring} onChange={v => u('annualRecurring', v)} min={500000} max={100000000} step={500000} format="currency" />
      <SliderInput label="Churn Rate" value={data.churnRate} onChange={v => u('churnRate', v)} min={1} max={25} step={1} format="percent" />
      <SliderInput label="Churn Reduction (relative)" value={data.churnReduction} onChange={v => u('churnReduction', v)} min={3} max={20} step={1} format="percent" />
      <SliderInput label="Active Customers" value={data.activeCustomers} onChange={v => u('activeCustomers', v)} min={100} max={50000} step={100} />
      <SliderInput label="Upsell %" value={data.upsellPct} onChange={v => u('upsellPct', v)} min={1} max={12} step={1} format="percent" />
      <SliderInput label="Avg Upsell Value" value={data.avgUpsellValue} onChange={v => u('avgUpsellValue', v)} min={50} max={5000} step={50} format="currency" />
      <SliderInput label="Partner Pipeline Value" value={data.partnerPipeline} onChange={v => u('partnerPipeline', v)} min={100000} max={20000000} step={100000} format="currency" />
      <SliderInput label="Channel Win Rate Lift (relative)" value={data.channelWinImprovement} onChange={v => u('channelWinImprovement', v)} min={3} max={20} step={1} format="percent" />
      <SliderInput label="Staff Count" value={data.staffCount} onChange={v => u('staffCount', v)} min={5} max={200} />
      <SliderInput label="Hours Saved / Staff / Week" value={data.hoursSaved} onChange={v => u('hoursSaved', v)} min={1} max={10} step={0.5} suffix=" hrs" />
      <SliderInput label="Annual Staff Cost" value={data.annualStaffCost} onChange={v => u('annualStaffCost', v)} min={30000} max={200000} step={5000} format="currency" />
    </div>
  );
}

function ConsultingInputs({ data, set }) {
  const u = (k, v) => set({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <SliderInput label="Proposal Pipeline Value" value={data.proposalPipeline} onChange={v => u('proposalPipeline', v)} min={1000000} max={50000000} step={500000} format="currency" />
      <SliderInput label="Win Rate Lift (relative)" value={data.winRateImprovement} onChange={v => u('winRateImprovement', v)} min={3} max={20} step={1} format="percent" />
      <SliderInput label="Total Consultants" value={data.totalConsultants} onChange={v => u('totalConsultants', v)} min={5} max={500} step={5} />
      <SliderInput label="Bench Days Saved / Consultant / Year" value={data.benchDaysSaved} onChange={v => u('benchDaysSaved', v)} min={1} max={15} />
      <SliderInput label="Daily Billing Rate" value={data.dailyBillingRate} onChange={v => u('dailyBillingRate', v)} min={400} max={3000} step={100} format="currency" />
      <SliderInput label="Active Accounts" value={data.activeAccounts} onChange={v => u('activeAccounts', v)} min={10} max={500} step={10} />
      <SliderInput label="Expansion %" value={data.expansionPct} onChange={v => u('expansionPct', v)} min={1} max={15} step={1} format="percent" />
      <SliderInput label="Avg Expansion Revenue" value={data.avgExpansionRev} onChange={v => u('avgExpansionRev', v)} min={5000} max={100000} step={5000} format="currency" />
      <SliderInput label="Number of Sellers" value={data.numSellers} onChange={v => u('numSellers', v)} min={1} max={100} />
      <SliderInput label="Hours Saved / Seller / Week" value={data.hoursSaved} onChange={v => u('hoursSaved', v)} min={1} max={10} step={0.5} suffix=" hrs" />
      <SliderInput label="Annual Seller Cost" value={data.annualSellerCost} onChange={v => u('annualSellerCost', v)} min={50000} max={300000} step={5000} format="currency" />
    </div>
  );
}

function EdTechInputs({ data, set }) {
  const u = (k, v) => set({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <SliderInput label="New Business Pipeline" value={data.newBusinessPipeline} onChange={v => u('newBusinessPipeline', v)} min={500000} max={30000000} step={500000} format="currency" />
      <SliderInput label="Win Rate Lift (relative)" value={data.winRateImprovement} onChange={v => u('winRateImprovement', v)} min={3} max={20} step={1} format="percent" />
      <SliderInput label="Renewal ARR" value={data.renewalARR} onChange={v => u('renewalARR', v)} min={500000} max={50000000} step={500000} format="currency" />
      <SliderInput label="Churn Rate" value={data.churnRate} onChange={v => u('churnRate', v)} min={1} max={25} step={1} format="percent" />
      <SliderInput label="Churn Reduction (relative)" value={data.churnReduction} onChange={v => u('churnReduction', v)} min={3} max={20} step={1} format="percent" />
      <SliderInput label="Active Accounts" value={data.activeAccounts} onChange={v => u('activeAccounts', v)} min={10} max={1000} step={10} />
      <SliderInput label="Expansion %" value={data.expansionPct} onChange={v => u('expansionPct', v)} min={1} max={12} step={1} format="percent" />
      <SliderInput label="Avg Expansion Revenue" value={data.avgExpansionRev} onChange={v => u('avgExpansionRev', v)} min={1000} max={50000} step={1000} format="currency" />
      <SliderInput label="Staff Count" value={data.staffCount} onChange={v => u('staffCount', v)} min={5} max={100} />
      <SliderInput label="Hours Saved / Staff / Week" value={data.hoursSaved} onChange={v => u('hoursSaved', v)} min={1} max={10} step={0.5} suffix=" hrs" />
      <SliderInput label="Annual Staff Cost" value={data.annualStaffCost} onChange={v => u('annualStaffCost', v)} min={30000} max={200000} step={5000} format="currency" />
    </div>
  );
}

const industryInputs = { saas: SaaSInputs, finance: FinanceInputs, b2c: B2CInputs, consulting: ConsultingInputs, edtech: EdTechInputs };

/* ═══════ MAIN COMPONENT ═══════ */

export default function ROICalculator() {
  const navigate = useNavigate();

  const [industry, setIndustry] = useState('saas');
  const [pricingModel, setPricingModel] = useState('seat');
  const [industryData, setIndustryData] = useState(industryDefaults.saas);

  // Pricing params
  const [seats, setSeats] = useState(20);
  const [accounts, setAccounts] = useState(50);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [creditPack, setCreditPack] = useState('growth');
  const [monthlyCredits, setMonthlyCredits] = useState(10000);

  const handleIndustryChange = (key) => {
    setIndustry(key);
    setIndustryData({ ...industryDefaults[key] });
  };

  const costParams = { seats, accounts, billingCycle, creditPack, monthlyCredits };
  const annualCost = useMemo(() => calcCost(pricingModel, costParams), [pricingModel, seats, accounts, billingCycle, creditPack, monthlyCredits]);
  const benefits = useMemo(() => benefitCalcs[industry](industryData), [industry, industryData]);
  const netGain = benefits.total - annualCost;
  const rawRoiPct = annualCost > 0 ? Math.round((netGain / annualCost) * 100) : 0;
  const roiPct = Math.min(rawRoiPct, ROI_DISPLAY_CAP);
  const roiCapped = rawRoiPct > ROI_DISPLAY_CAP;
  const paybackMonths = benefits.total > 0 ? Math.round((annualCost / (benefits.total / 12)) * 10) / 10 : 0;

  // Baseline win/conversion rate for the assumptions panel
  const baselineRate = industryData.baselineWinRate ?? industryData.baselineConversion ?? null;

  const IndustryPanel = industryInputs[industry];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title="ROI Calculator, Forsyt Revenue OS"
        description="Calculate the ROI of Forsyt for your revenue team. Industry-specific models for SaaS, Financial Services, B2C, Consulting, and EdTech."
        path="/resources/roi-calculator"
      />

      {/* Hero */}
      <section className="pt-16 pb-10 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Calculator className="w-10 h-10 text-blue-600 mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-testid="roi-headline">
            Forsyt ROI Calculator
          </h1>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Select your industry and pricing model. Adjust the inputs to match your org and see real-time ROI projections.
          </p>
        </div>
      </section>

      {/* Industry Selector */}
      <section className="py-6">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Select your industry</p>
          <div className="flex flex-wrap gap-2">
            {industries.map((ind) => {
              const Icon = ind.icon;
              return (
                <button
                  key={ind.key}
                  onClick={() => handleIndustryChange(ind.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full border transition-all ${
                    industry === ind.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                  data-testid={`industry-${ind.key}`}
                >
                  <Icon className="w-4 h-4" />
                  {ind.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Calculator Body */}
      <section className="py-8">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-8">

            {/* Left: Inputs (3 cols) */}
            <div className="lg:col-span-3 space-y-8">

              {/* Pricing Model */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200" data-testid="pricing-section">
                <h2 className="text-base font-bold text-gray-900 mb-4">Pricing Model</h2>
                <div className="flex gap-2 mb-5">
                  {pricingModels.map((pm) => (
                    <button
                      key={pm.key}
                      onClick={() => setPricingModel(pm.key)}
                      className={`px-4 py-2 text-sm font-medium rounded-full border transition-all ${
                        pricingModel === pm.key
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                      }`}
                      data-testid={`pricing-${pm.key}`}
                    >
                      {pm.label}
                    </button>
                  ))}
                </div>

                {pricingModel === 'seat' && (
                  <div className="space-y-4">
                    <SliderInput label="Number of AEs (Seats)" value={seats} onChange={setSeats} min={1} max={300} />
                    <p className="text-xs text-gray-500">
                      Tier: {seatTiers.find(t => seats >= t.min && seats <= t.max)?.label || 'Scale (200+)'}, ${seatTiers.find(t => seats >= t.min && seats <= t.max)?.rate || 15}/seat/mo
                    </p>
                  </div>
                )}

                {pricingModel === 'account' && (
                  <div className="space-y-4">
                    <SliderInput label="Accounts Managed" value={accounts} onChange={setAccounts} min={5} max={500} step={5} />
                    <div className="flex gap-2">
                      {['annual', 'monthly'].map((cycle) => (
                        <button
                          key={cycle}
                          onClick={() => setBillingCycle(cycle)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
                            billingCycle === cycle ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-white text-gray-500 border-gray-200'
                          }`}
                        >
                          {cycle === 'annual' ? 'Annual ($199/acct/mo)' : 'Monthly ($239/acct/mo)'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {pricingModel === 'credit' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 mb-2">Select credit pack:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {creditPacks.map((pack) => (
                        <button
                          key={pack.key}
                          onClick={() => setCreditPack(pack.key)}
                          className={`p-3 text-left rounded-xl border transition-all ${
                            creditPack === pack.key ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:border-blue-200'
                          }`}
                        >
                          <p className="text-sm font-bold text-gray-900">{pack.label}</p>
                          <p className="text-xs text-gray-500">
                            {pack.key === 'payg' ? '$0.003/credit' : `$${pack.price}/mo, ${pack.credits.toLocaleString()} credits`}
                          </p>
                        </button>
                      ))}
                    </div>
                    {creditPack === 'payg' && (
                      <SliderInput label="Estimated Monthly Credits" value={monthlyCredits} onChange={setMonthlyCredits} min={1000} max={200000} step={1000} />
                    )}
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Annual Forsyt Cost</span>
                    <span className="text-lg font-bold text-gray-900" data-testid="annual-cost">{fmt(annualCost)}</span>
                  </div>
                </div>
              </div>

              {/* Industry Inputs */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200" data-testid="benefit-inputs">
                <h2 className="text-base font-bold text-gray-900 mb-1">
                  {industries.find(i => i.key === industry)?.label}, Benefit Inputs
                </h2>
                <p className="text-xs text-gray-500 mb-5">Adjust sliders to match your organization. Defaults are industry benchmarks.</p>
                <IndustryPanel data={industryData} set={setIndustryData} />
              </div>
            </div>

            {/* Right: Results (2 cols) */}
            <div className="lg:col-span-2 space-y-4">
              <div className="sticky top-4 space-y-4">

                {/* ROI Hero */}
                <div className="bg-blue-600 text-white rounded-2xl p-6" data-testid="roi-total">
                  <p className="text-sm font-medium text-blue-200 mb-1">Year-1 ROI</p>
                  <p className="text-5xl font-bold">{roiPct.toLocaleString()}%{roiCapped ? '+' : ''}</p>
                  <p className="text-sm text-blue-200 mt-2">
                    {roiCapped
                      ? 'Capped for display, see assumptions below'
                      : 'Return on Forsyt investment'}
                  </p>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <DollarSign className="w-5 h-5 text-emerald-500 mb-1" />
                    <p className="text-xs text-gray-500">Net Annual Gain</p>
                    <p className="text-lg font-bold text-gray-900" data-testid="net-gain">{fmt(netGain)}</p>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <Clock className="w-5 h-5 text-amber-500 mb-1" />
                    <p className="text-xs text-gray-500">Payback Period</p>
                    <p className="text-lg font-bold text-gray-900" data-testid="payback">{paybackMonths} months</p>
                  </div>
                </div>

                {/* Total Benefit */}
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-gray-900">Total Annual Benefit</span>
                    <span className="text-lg font-bold text-emerald-600" data-testid="total-benefit">{fmt(benefits.total)}</span>
                  </div>
                  <div className="space-y-2">
                    {benefits.items.map((item, i) => (
                      <div key={i} className="flex items-center justify-between" data-testid={`benefit-line-${i}`}>
                        <span className="text-sm text-gray-600">{item.label}</span>
                        <span className="text-sm font-semibold text-gray-900">{fmt(item.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Annual Cost */}
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Annual Forsyt Cost</span>
                    <span className="text-sm font-bold text-red-500">{fmt(annualCost)}</span>
                  </div>
                </div>

                {/* Assumptions */}
                <details className="bg-white border border-gray-200 rounded-xl" data-testid="roi-assumptions">
                  <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between text-sm font-bold text-gray-900 hover:bg-gray-50 rounded-xl">
                    <span>How we calculate this</span>
                    <span className="text-xs font-medium text-blue-600">View assumptions</span>
                  </summary>
                  <div className="px-4 pb-4 pt-1 space-y-3 text-xs text-gray-600 leading-relaxed">
                    {baselineRate !== null && (
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        <span>
                          <strong className="text-gray-900">Baseline rate:</strong> {fmtPct(baselineRate)} (industry benchmark used as the starting point for {industry === 'b2c' ? 'conversion' : 'win-rate'} lift calculations).
                        </span>
                      </div>
                    )}
                    <div className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      <span>
                        <strong className="text-gray-900">Pipeline / conversion gain</strong> = pipeline × baseline rate × relative lift. We treat &ldquo;lift&rdquo; as a relative improvement on the baseline rate, not as an absolute conversion of total pipeline.
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      <span>
                        <strong className="text-gray-900">Productivity gain</strong> applies a {Math.round(REALISATION_FACTOR * 100)}% realisation factor, i.e. only {Math.round(REALISATION_FACTOR * 100)}% of hours saved are counted as recovered cost. CFO-defensible &ldquo;soft savings&rdquo; haircut.
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      <span>
                        <strong className="text-gray-900">Retention gain</strong> = revenue base × current churn rate × relative churn reduction. Defaults reflect typical first-year impact from revenue-execution tooling (5, 15% relative reduction).
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      <span>
                        <strong className="text-gray-900">Year-1 only.</strong> Multi-year impact (retention compounding, expanded usage) is not modelled here, actual NPV is typically higher.
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-gray-400">•</span>
                      <span>
                        <strong className="text-gray-900">Pricing.</strong> Annual cost reflects current Forsyt list pricing for the selected model and tier. Enterprise commitments may negotiate different terms in the order form.
                      </span>
                    </div>
                    <p className="pt-2 text-[11px] text-gray-400 italic">
                      Numbers shown are directional estimates for internal business-case modelling, not a contractual commitment.
                    </p>
                  </div>
                </details>

                {/* CTA */}
                <div className="pt-2 space-y-3">
                  <a
                    href="https://calendar.app.google/nswB9pt6ALLZ8xoy7"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-full px-6 py-3 text-sm font-bold bg-blue-600 hover:bg-blue-500 text-white rounded-full transition-all shadow-lg"
                    data-testid="roi-cta-demo"
                  >
                    Book a Demo <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="flex items-center justify-center w-full px-6 py-3 text-sm font-semibold border-2 border-gray-200 hover:border-blue-400 bg-white rounded-full transition-all"
                    data-testid="roi-cta-sandbox"
                  >
                    Try the Sandbox
                  </button>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
